import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { EngineOverlay } from '@/components/EngineOverlay';

/**
 * BODYMAX Engine Transition Context
 * 
 * Controls the full first-plan transition:
 *   idle → generating → navigating → hydrating → ready_to_reveal → idle
 * 
 * The overlay stays visible across route changes and only fades once
 * the destination page signals readiness via `signalDestinationReady()`.
 */
const EngineContext = createContext(null);

export function EngineProvider({ children }) {
  const [visible, setVisible] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [mode, setMode] = useState('build');
  const [week, setWeek] = useState(1);
  const [minutes, setMinutes] = useState(null);

  const [hasRetry, setHasRetry] = useState(false);
  const generationFnRef = useRef(null);
  const onSuccessRef = useRef(null);
  const onErrorRef = useRef(null);
  const onDismissRef = useRef(null);
  const onExitRef = useRef(null);
  const onRetryRef = useRef(null);
  const destinationReadyResolveRef = useRef(null);

  const trigger = useCallback(({ subtitle: sub, mode: m, week: wk, minutes: mins, generationFn, onSuccess, onError, onDismiss, onExitToOnboarding, onRetry }) => {
    generationFnRef.current = generationFn;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onDismissRef.current = onDismiss;
    onExitRef.current = onExitToOnboarding;
    onRetryRef.current = onRetry;
    destinationReadyResolveRef.current = null;
    setHasRetry(!!onRetry);
    setSubtitle(sub || 'Building your first personalized program');
    setMode(m || 'build');
    setWeek(wk || 1);
    setMinutes(Array.isArray(mins) ? mins : null);
    setVisible(true);
  }, []);

  /**
   * Called by the destination page (e.g. Workout.js) when its critical
   * data is loaded and paint-ready. The overlay waits for this signal
   * before fading out.
   */
  const signalDestinationReady = useCallback(() => {
    console.log('[ENGINE_CTX] destination ready signal received');
    if (destinationReadyResolveRef.current) {
      destinationReadyResolveRef.current();
      destinationReadyResolveRef.current = null;
    }
  }, []);

  /**
   * Returns a promise that resolves when signalDestinationReady() is called,
   * or after a timeout (fallback to prevent permanent stuck overlay).
   */
  const waitForDestinationReady = useCallback(() => {
    return new Promise((resolve) => {
      destinationReadyResolveRef.current = resolve;
      // Fallback timeout — never stay stuck longer than 5 seconds waiting for destination
      setTimeout(() => {
        if (destinationReadyResolveRef.current) {
          console.log('[ENGINE_CTX] destination ready TIMEOUT — releasing overlay');
          destinationReadyResolveRef.current = null;
          resolve();
        }
      }, 5000);
    });
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    onDismissRef.current?.();
    generationFnRef.current = null;
    onSuccessRef.current = null;
    onErrorRef.current = null;
    onDismissRef.current = null;
    onExitRef.current = null;
    onRetryRef.current = null;
    destinationReadyResolveRef.current = null;
  }, []);

  const stableOnExit = useCallback(() => {
    onExitRef.current?.();
  }, []);

  // Stable wrappers
  const stableGenerationFn = useCallback(async () => {
    if (!generationFnRef.current) throw new Error('No generation function provided');
    return generationFnRef.current();
  }, []);

  const stableOnSuccess = useCallback((data) => {
    onSuccessRef.current?.(data);
  }, []);

  const stableOnError = useCallback((err) => {
    onErrorRef.current?.(err);
  }, []);

  const stableOnRetry = useCallback(async () => {
    if (!onRetryRef.current) throw new Error('No retry function provided');
    return onRetryRef.current();
  }, []);

  return (
    <EngineContext.Provider value={{ trigger, visible, signalDestinationReady }}>
      {children}
      <EngineOverlay
        visible={visible}
        subtitle={subtitle}
        mode={mode}
        week={week}
        minutes={minutes}
        generationFn={visible ? stableGenerationFn : null}
        onSuccess={stableOnSuccess}
        onError={stableOnError}
        onDismiss={handleDismiss}
        onExitToOnboarding={stableOnExit}
        onRetry={hasRetry ? stableOnRetry : null}
        waitForDestinationReady={waitForDestinationReady}
      />
    </EngineContext.Provider>
  );
}

export function useEngine() {
  const ctx = useContext(EngineContext);
  if (!ctx) throw new Error('useEngine must be used within EngineProvider');
  return ctx;
}
